import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MaybeTooltip } from '@modernloop/shared/components';
import { Avatar, Checkbox, List, ListItem, ListItemIcon, ListSubheader, Stack, Theme, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { DynamicAudience, OptionRenderer_EmployeesFragment } from 'src/generated/mloop-graphql';

import { HoverPopover } from './HoverPopover';
import { DynamicAudienceChipIconVariant, getDacIcon, getSmartOptionLabel } from './utils';

type Fragments = {
  employees: OptionRenderer_EmployeesFragment[] | undefined | null;
};

type Props = {
  type: DynamicAudience;
  isChecked?: boolean;
  isDisabled?: boolean;
  disableMultiSelect?: boolean;
};

const getEmployeeName = (employee?: OptionRenderer_EmployeesFragment) =>
  employee?.fullName ?? employee?.givenName ?? employee?.familyName ?? employee?.email ?? '';

export const OptionRenderer: FCWithFragments<Fragments, Props> = ({
  employees,
  type,
  isChecked,
  isDisabled,
  disableMultiSelect,
}) => {
  const { t } = useTranslation();
  const multipleEmployees = !!employees && !isEmpty(employees) && employees.length > 1;
  const singleEmployeeInArray = !!employees && !isEmpty(employees) && employees.length === 1;

  const rowLabel = useMemo(() => {
    let jsx = '';

    if (multipleEmployees) {
      jsx = `(${employees.length} people)`;
    } else if (singleEmployeeInArray) {
      jsx = `(${getEmployeeName(employees?.[0])})`;
    }
    return jsx;
  }, [employees, multipleEmployees, singleEmployeeInArray]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      columnGap={1}
      sx={{
        width: '100%',
      }}
    >
      {!disableMultiSelect && <Checkbox checked={isChecked} disabled={isDisabled} />}{' '}
      <ListItemIcon
        sx={{
          color: (theme: Theme) => (isDisabled ? theme.palette.action.disabled : theme.palette.primary.main),
        }}
      >
        {getDacIcon(type, DynamicAudienceChipIconVariant.Inherit)}
      </ListItemIcon>
      {multipleEmployees ? (
        <HoverPopover
          title={
            <List>
              <ListSubheader sx={{ px: 0 }}>{t('hiring-dashboard-common:common.select.inThisGroup')}</ListSubheader>
              <Stack
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                }}
              >
                {employees?.map((employee) => (
                  <ListItem key={employee.id} disableGutters>
                    <Stack direction="row" alignItems="center" columnGap={1}>
                      <Avatar src={employee.internalProfilePictureUrl ?? ''} alt={employee.fullName || ''} />
                      <Typography color="text.secondary">{employee.fullName}</Typography>
                    </Stack>
                  </ListItem>
                ))}
              </Stack>
            </List>
          }
        >
          <Typography sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {`${getSmartOptionLabel(type, t)} ${rowLabel}`}
          </Typography>
        </HoverPopover>
      ) : (
        <MaybeTooltip
          title={`${getSmartOptionLabel(type, t)} ${rowLabel}`}
          label={`${getSmartOptionLabel(type, t)} ${rowLabel}`}
          placement="top"
        />
      )}
    </Stack>
  );
};

OptionRenderer.fragments = {
  employees: gql`
    fragment OptionRenderer_employees on Employee {
      id
      fullName
      givenName
      familyName
      internalProfilePictureUrl
      email
      isArchived
      isAtsDisabled
      hasAtsId
      isDisabled
      orgId
    }
  `,
};
